import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { TriangleAlert } from "lucide-react";
import { useStore } from "@/lib/store";
import { useAuth } from "@/lib/authContext";
import { listTasks } from "@/lib/tasksApi";
import { cn } from "@/lib/utils";

/** HITL chip — live count of HUMAN-owned, not-done tasks.
 *  Polls /api/tasks on mount + every 30s + on route change.
 */
export default function HitlChip({ className }) {
  const openHitl = useStore((s) => s.openHitl);
  const setTasksList = useStore((s) => s.setTasksList);
  const tasksInStore = useStore((s) => s.tasks);
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const data = await listTasks();
      setTasksList(data || []);
    } catch {
      /* swallow — chip just hides */
    } finally {
      setReady(true);
    }
  }, [isAuthenticated, setTasksList]);

  useEffect(() => {
    refresh();
    if (!isAuthenticated) return;
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh, isAuthenticated]);

  // Refresh on route changes (so after /tasks updates, other pages see it fast).
  useEffect(() => {
    refresh();
  }, [location.pathname, refresh]);

  const count = useMemo(
    () => (tasksInStore || []).filter((t) => t.owner === "HUMAN" && t.status !== "done").length,
    [tasksInStore],
  );

  if (!ready || count === 0) return null;

  return (
    <button
      type="button"
      data-testid="hitl-chip"
      data-hitl-count={count}
      onClick={openHitl}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border border-[#FFE0B2] bg-[#FFF7EE] px-3 py-1.5 text-[12px] font-semibold text-orange-700 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_18px_-10px_rgba(249,115,22,0.45)]",
        className,
      )}
    >
      <TriangleAlert className="h-3.5 w-3.5" strokeWidth={2.4} />
      <span>{count} HITL</span>
    </button>
  );
}
